<template>
    <div class="payroll-details-header-wrapper">
        <div class="payroll-header-container" style="margin: 20px auto;">
   <div class="dropdowns-container">
  <div class="admin-access-section">
    <HeaderEmployeeDropdown
      v-if="!isEmployeeSearchHidden"
      :items="employeeList"
      labelKey="username"
      searchable
      @select="selectEmployee"
      :placeholder="this.employee.employeeName || 'Select Employee'"
    />
  </div>
<div style="width: 300px" v-if="isAdmin"></div>
  <div class="search-group">
    <div class="searchable-1">
      <SearchableDropdown
        :items="months.map(month => ({ name: month }))"
        labelKey="name"
        :placeholder="this.employee.month || selectedMonth || 'Select Month'"
        @select="selectMonth"
      />
    </div>

    <div class="searchable-2">
      <SearchableDropdown
        :items="years.map(year => ({ name: year }))"
        labelKey="name"
        :placeholder="Number(this.employee.year || selectedYear ) || 'Select Year'"
        @select="selectYear"
      />
    </div>
  </div>

  <div class="payroll-header-actions">
    <button class="btn btn-outline" @click="filterPayrollData" :disabled="isSearching">
      <i class="fa fa-search payroll-header-search-icon"></i>
    </button>

    <button v-if="!isAdmin" class="btn btn-outline" @click="$router.push({ path: '/payroll', query: { tab: 'expenses' } })">
      My Expenses
    </button>

    <button v-if="this.isAdmin" class="btn btn-outline export-icon-button" @click="exportToExcel">
      <span class="export-icon">↓</span>
      Export
    </button>
  </div>
</div>
        </div>

        <div v-if="isSearching" class="payroll-search-overlay">
          <div class="payroll-loader-box">
            <DoubleLoader class="payroll-loader" />
          </div>
        </div>
    </div>
</template>

<script>
import SearchableDropdown from 'src/components/UIComponents/Dropdowns/SearchableDropdown.vue';
import HeaderEmployeeDropdown from 'src/components/UIComponents/HeaderEmployeeDropdown.vue';
import DoubleLoader from "src/components/UIComponents/loaders/DoubleLoader.vue";
import firebase from "firebase/app";
import "firebase/database";

export default {
    name: 'PayrollManagement',
    components: {
        SearchableDropdown,
        HeaderEmployeeDropdown,
        DoubleLoader
    },
    props: ["employee"],
    data() {
        const user = this.$store.state.auth.user;
        return {
            isAdmin: user && user.role === "admin",
            selectedYear: this.employee.year || null,
            selectedMonth: this.employee.month || null,
            selectedEmployee: null,
            employeeList: [],
            months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
            years: Array.from({
                length: 10
            }, (_, i) => new Date().getFullYear() - 5 + i),
            payrollSummary: [],
            employeeId: user.uid,
            previousSelectedMonth: null,
            previousSelectedYear: null,
            isManualMonthSelected: false,
            isManualYearSelected: false,
            isSearching: false
        }
    },
    created() {
        const allUsers = this.$store.state.auth.allUsers;
        const companyUserIds = this.$store.state.auth.company.users.map(user => user.uid);
        this.employeeList = allUsers.filter(user => companyUserIds.includes(user.uid));

        if (!this.isAdmin) {
            this.selectedMonth = localStorage.getItem("payrollMonth") || this.employee.month;
            this.selectedYear = localStorage.getItem("payrollYear") || this.employee.year;
        } else {
            this.selectedMonth = this.employee.month;
            this.selectedYear = this.employee.year;
        }
        this.saveMonthYearToLocalStorage(this.selectedMonth, this.selectedYear);
    },
    methods: {
        selectEmployee(employee) {
            this.selectedEmployee = employee || this.employee;
        },
        selectMonth(month) {
            this.selectedMonth = month.name;
            this.previousSelectedMonth = month.name;
            this.isManualMonthSelected = true;
            if (!this.isAdmin) {
                localStorage.setItem("payrollMonth", month.name);
            }
        },
        selectYear(year) {
            this.selectedYear = year.name;
            this.previousSelectedYear = year.name;
            this.isManualYearSelected = true;
            if (!this.isAdmin) {
                localStorage.setItem("payrollYear", year.name);
            }
        },
        exportToExcel() {
            this.$emit("export-excel");
        },
        async filterPayrollData() {
            const selectedEmpId = !this.isAdmin && this.employeeId || this.selectedEmployee && this.selectedEmployee.uid || this.employee.employeeId;
            const searchCriteria = {
                employeeId: selectedEmpId,
                month: this.isManualMonthSelected ? this.selectedMonth : this.employee.month,
                year: this.isManualYearSelected ? this.selectedYear : this.employee.year
            };

            if (!searchCriteria.employeeId || !searchCriteria.month || !searchCriteria.year) {
                console.warn("Please select employee, month and year");
                return;
            }

            // Use a local flag instead of the shared store `loading`: the page hides
            // its header while that flag is set, which would destroy this component
            // and wipe the employee/month/year the admin just selected.
            this.isSearching = true;

            try {
                const payrollRef = firebase.database().ref(`payroll/${searchCriteria.year}/${searchCriteria.month}`)
                    .orderByChild('employeeId')
                    .equalTo(searchCriteria.employeeId);

                const snapshot = await payrollRef.once('value');

                if (snapshot.exists()) {
                    const payrollData = Object.values(snapshot.val())[0];
                    const isProcessed = payrollData.status &&
                        payrollData.status.toLowerCase().trim() === "processed";
                    if (isProcessed || this.isAdmin) {
                        this.$store.commit('setSelectedEmployeePayroll', payrollData);
                    } else {
                        await this.createNewPayroll(searchCriteria);
                    }
                } else {
                    await this.createNewPayroll(searchCriteria);
                }
            } catch (error) {
                console.error("Error filtering payroll data:", error);
                swal("Oops!", error.message || "Failed to load payroll data.", "error");
            } finally {
                this.isSearching = false;
            }
        },

        async createNewPayroll(searchCriteria = {}) {
            const selectedYear = searchCriteria.year || this.employee.year;
            const selectedMonth = searchCriteria.month || this.employee.month;
            const selectedEmployeeId = searchCriteria.employeeId || this.employee.employeeId;

            try {
                const company = this.$store.state.auth.company;

                const companyUserIds = company.users.map(function (user) {
                    return user.uid;
                });

                const allEmployees = this.$store.state.auth.allUsers.filter(function (user) {
                    return companyUserIds.indexOf(user.uid) !== -1;
                });

                const selectedEmployeeRecord = allEmployees.find(function (user) {
                    return user.uid === selectedEmployeeId;
                });

                if (!this.isAdmin && selectedEmployeeRecord) {
                    swal("Oops!", `No processed payroll found for ${selectedMonth}, ${selectedYear}.`, "error");
                    return;
                }

                const constants = this.$store.state.constants
                const newPayroll = await this.$store.dispatch('createPayrollForEmployee', {
                    emp: selectedEmployeeRecord || this.employee,
                    selectedYear: selectedYear,
                    selectedMonth: selectedMonth,
                    constantsCollection: constants && constants.constant ? constants.constant : constants
                });
                if (newPayroll) {
                    this.$store.commit('setSelectedEmployeePayroll', newPayroll);
                } else {
                    console.warn("No new payroll created.");
                    swal("Oops!", `No processed payroll found for ${selectedMonth}, ${selectedYear}.`, "error");
                }

            } catch (error) {
                console.error("Error creating new payroll:", error);
                throw error;
            }
        },
        saveMonthYearToLocalStorage(month, year) {
            if (month) localStorage.setItem("payrollMonth", month);
            if (year) localStorage.setItem("payrollYear", year);
        }
    },
    computed: {
        isSearchDisabled() {
            const empId = this.employeeId || this.selectedEmployee && this.selectedEmployee.uid;
            return !empId || !this.selectedMonth || !this.selectedYear;
        },
        isEmployeeSearchHidden() {
            return this.$store.state.auth.user.role !== "admin";
        }
    },
}
</script>

<style scoped>
.btn-primary {
    background-color: #007bff;
    color: white;
    padding: 8px 12px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.btn-primary:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
}


.dropdowns-container {
  display: flex;
  flex-wrap: wrap;
  padding: 1rem;
  max-width: 100%;
  align-items: flex-start;
}

.search-group {
  display: flex;
  align-items: center;
  gap: 33px;
  flex-wrap: wrap;
}

.payroll-details-header-wrapper {
  position: relative;
}

.payroll-loader {
  display: flex;
  justify-content: center;
  align-items: center;
}

.payroll-loader-box {
  width: 48px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.payroll-search-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20;
}

@media (max-width: 1315px) {
  .dropdowns-container {
    flex-direction: column;
    
  }
  .payroll-header-actions{
    margin-top:10px;
  }

  .search-group {
    display: flex;
    align-items: center;
    margin-top: 60px;
    margin-left: 0px;
     gap: 10px;
  }
}

@media (max-width: 720px) {
  .search-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/PayrollManagement/PayrollDetails/Header/index.vue